import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { deleteAdminToken } from "../slices/generalSlice";

let isRedirectingToLogin = false;

export const CONSTANTS = {
  ROUTES: [
    "/dashboard",
    "/drivers",
    "/users",
    "/admins",
    "/rides",
    "/all-rides",
    "/completed-rides",
    "/pending-rides",
    "/cancelled-rides",
    "/vehicle-types",
    "/vehicle-info",
    "/financials",
    "/faqs",
  ],
  SECURE_DOCUMENTS_PREFIX_URL: "https://dvour.s3.amazonaws.com/",
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BASE_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().general.adminToken;

    // If we have a token set in state, let's assume that we should be passing it.
    if (token) {
      headers.set("authorization", `${token}`);
    }

    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (
    (result?.error?.status === 403 || result?.error?.status === 401) &&
    !isRedirectingToLogin &&
    window.location.pathname !== "/login"
  ) {
    isRedirectingToLogin = true;
    api.dispatch(deleteAdminToken());
    window.location.replace("/login");
  }
  return result;
};

// Define a service using a base URL and expected endpoints
export const Api = createApi({
  reducerPath: "apiData",
  baseQuery: baseQueryWithReauth,
  keepUnusedDataFor: 30,
  endpoints: (builder) => ({
    getDriverCount: builder.query({
      query: () => `admin/driver-count`,
    }),
    getDriverCountByCategory: builder.query({
      query: () => `admin/driver-count-by-category`,
    }),
    getRiderCount: builder.query({
      query: () => `admin/user-count`,
    }),
    getVehicleTypes: builder.query({
      query: () => `admin/vehicle-types`,
    }),
    getVehicleInfo: builder.query({
      query: () => `/vehicle-info`,
    }),
    getUsersLocalizationCategories: builder.query({
      query: () => `admin/get-users-localization-categories`,
    }),
    getDriversLocalizationCategories: builder.query({
      query: () => `admin/get-drivers-localization-categories`,
    }),
    getTripStats: builder.query({
      query: () => `admin/get-trips-stats`,
    }),
    // getAllDrivers: builder.query({
    //   query: () => `admin/get-all-drivers`,
    // }),
    getAllDrivers: builder.query({
      query: ({ limit = 0, skip = 0, fromDate, toDate, status, city } = {}) => ({
        url: `admin/get-all-drivers`,
        params: {
          limit: limit || 0,
          skip: skip || 0,
          fromDate: fromDate,
          toDate: toDate,
          status: status,
          city: city
        }
      }),
    }),
    getAllDriversExport: builder.query({
      query: ({ fromDate, toDate, status, city } = {}) => ({
        url: `admin/get-all-drivers`,
        params: {
          limit: 1000,
          skip: 0,
          fromDate: fromDate,
          toDate: toDate,
          status: status,
          city: city
        }
      }),
    }),

    getDriversRegReport: builder.query({
      query: ({ city, vehicle, isOnline, fromDate, toDate } = {}) => ({
        url: `admin/get-all-vehicles`,
        params: {
          city: city,
          vehicle: 'All',
          //isOnline: false,
          fromDate: fromDate,
          toDate: toDate,

        }
      }),
    }),

    getDriversEarningReport: builder.query({
      query: ({ fromDate, toDate, refCode, phoneNumber, vehicleNo } = {}) => ({
        url: `admin/driver-report`,
        params: {
          fromDate: fromDate,
          toDate: toDate,
          refCode: refCode || '',
          phoneNumber: phoneNumber || '',
          vehicleNo: vehicleNo || '',
        }
      }),
    }),

    getDriversEarningsReport: builder.query({
      query: ({sortBy,sortOrder, limit, skip,  startDate, endDate, refCode, search, city } = {}) => ({
        url: `driver/admin-driver-performance`,
        params: {
          sortBy: 'acceptedTrips' || '',
          sortOrder: 'desc' || '',
          limit: limit || 0,
          skip: skip || 0,
          startDate: startDate,
          endDate: endDate,
          refCode: refCode || '',
          search: search || '',
          city: city || '',
        }
      }),
    }),


    getAllDriversOnlineVehicleWise: builder.query({
      query: ({ city, vehicle } = {}) => ({
        url: `admin/get-all-vehicles`,  //vehicle/get-all

        params: {
          city: city,
          vehicle: vehicle,
          isOnline: true,
        }
      }),
    }),

    getRentalAllBooking: builder.query({
      query: () => `car-rental/admin/bookings`,
    }),


    getAllRentalVehicles: builder.query({
      query: ({ limit = 10, skip = 0 } = {}) => ({
        url: `admin/get-all-rental-vehicles`,
        params: {
          limit,
          skip
        }
      }),
    }),


    getRentalAvailableDrivers: builder.query({
      query: () => `car-rental/admin/drivers`,
    }),

    getAllDriversSearch: builder.query({
      query: ({ phoneNumber, name } = {}) => ({
        url: `admin/get-all-drivers`,
        params: {
          phoneNumber: phoneNumber,
          name: name
        }
      }),
    }),
    getSingleDriverWallet: builder.query({
      query: (driverId) => ({
        url: `wallet/driver/${driverId}`,
      }),
    }),


    // getAllUploadedPayments: builder.query({
    //   query: () => `deposit`,
    // }),    
    getAllUploadedPayments: builder.query({
      query: ({ limit = 10, skip = 0 } = {}) => ({
        url: `deposit`,
        params: {
          limit,
          skip
        }
      }),
    }),
    getAllUsers: builder.query({
      query: () => `admin/get-all-users`,
    }),
    getAllAdmins: builder.query({
      query: () => `admin/get-all-admins`,
    }),
    getDriverById: builder.query({
      query: (driverId) => ({
        url: `driver/get-by-id/${driverId}`,
        method: 'GET',
      }),
    }),
    getCourierDriverList: builder.query({
      query: (tripId) => ({
        url: `trips/active-drivers/${tripId}`,
        method: 'GET',
      }),
    }),
    vehicleCreateByAdminById: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `vehicle/create-by-admin/${id}`,
          method: "POST",
          body,
        };
      },
    }),
    getVehicleSwitchById: builder.query({
      query: (type) => ({
        url: `vehicle/allowed-switches?type=${encodeURIComponent(type)}`,
        method: 'GET',
      }),
    }),

    getDriverVehicleById: builder.query({
      query: (driverId) => ({
        url: `admin/get-driver-vehicle/${driverId}`,
        method: 'GET',
      }),
    }),

    getVehicleById: builder.query({
      query: (vehicleId) => ({
        url: `vehicle/get-by-id/${vehicleId}`,
        method: 'GET',
      }),
    }),

    getDriverProfileById: builder.query({
      query: (driverId) => ({
        url: `admin/get-driver-profile/${driverId}`,
        method: 'GET',
      }),
    }),
    getDriverDocumentsById: builder.query({
      query: (driverId) => ({
        url: `document/get-by-driver/${driverId}`,
        method: 'GET',
      }),
    }),
    getDriversLogById: builder.query({
      query: (driverId) => ({
        url: `driver-logs/driver/${driverId}`,
        method: 'GET',
      }),
    }),
    getSubscribedDriverbyId: builder.mutation({
      query: (driverId) => ({
        url: `subscription/${driverId}`,
        method: 'GET',
      }),
    }),
    updateDriverDocByAdminId: builder.mutation({
      query({ docId, driverId, ...body }) {
        return {
          url: `document/update-by-admin/${docId}/${driverId}`,
          method: "PUT",
          body,
        };
      },
    }),
    createVehicleByAdmin: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `document/create-by-admin/${id}`,
          method: "POST",
          body,
        };
      },
    }),


    getAllTrips: builder.query({
      query: ({ limit, skip, status, service, fromDate, toDate, isTestRide = true }) => ({
        url: `admin/get-all-trips`,
        params: {
          limit: limit || 0,
          skip: skip || 0,
          status: status,
          service: service,
          fromDate: fromDate,
          toDate: toDate,
          isTestRide: isTestRide,
        }
      }),
    }),

    getDriversWithdrawal: builder.query({
      query: ({ limit, skip, fromDate, toDate, }) => ({
        url: `wallet/completed/`,
        params: {
          limit: limit || 0,
          skip: skip || 0,
          fromDate: fromDate,
          toDate: toDate,          
        }
      }),
    }),

    getAllTripsExport: builder.query({
      query: ({ status, service, fromDate, toDate, isTestRide = true }) => ({
        url: `admin/get-all-trips`,
        params: {
          limit: 0, // 0 means get all records
          skip: 0,
          status,
          service,
          fromDate,
          toDate,
          isTestRide: isTestRide,
        }
      }),
    }),
    getDriversWallet: builder.query({
      query: ({ limit, skip,fromDate,toDate }) => ({
        url: `wallet`,
        params: {
          limit: limit || 0,
          skip: skip || 0,
          withdrawalRequests:true,
          fromDate:fromDate,
          toDate:toDate,
        }
      }),
    }),



    // getAllTrips: builder.query({
    //   query: () => `admin/get-all-trips`,
    // }),
    loginUser: builder.mutation({
      query(body) {
        return {
          url: `admin/login`,
          method: "POST",
          body,
        };
      },
    }),
    updateSingleAdmin: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `admin/update-admin/${id}`,
          method: "PATCH",
          body,
        };
      },
    }),
    updateSingleDriver: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `admin/update-driver/${id}`,
          method: "PATCH",
          body,
        };
      },
    }),
    updateSingleUser: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `admin/update-user/${id}`,
          method: "PATCH",
          body,
        };
      },
    }),
    createUser: builder.mutation({
      query(body) {
        return {
          url: `admin/create`,
          method: "POST",
          body,
        };
      },
    }),
    createDriverSubscription: builder.mutation({
      query(body) {
        return {
          url: `subscription/subscribe`,
          method: "POST",
          body,
        };
      },
    }),
    UpdateDriverWithdrawl: builder.mutation({
      query(body) {
        return {
          url: `wallet/update`,
          method: "PUT",
          body,
        };
      },
    }),
    UpdateDriverDeposit: builder.mutation({
      query(body) {
        return {
          url: `deposit/update`,
          method: "PUT",
          body,
        };
      },
    }),
    logoutUser: builder.mutation({
      query() {
        return {
          url: `admin/logout`,
          method: "POST",
        };
      },
    }),
    deleteUser: builder.mutation({
      query(id) {
        return {
          url: `admin/delete-user/${id}`,
          method: "DELETE",
        };
      },
    }),
    updateDriverPassword: builder.mutation({
      query(body) {
        return {
          url: `admin/update-driver-password`,
          method: "PATCH",
          body,
        };
      },
    }),
    getVehicleTypeStats: builder.query({
      query: () => `admin/get-vehicle-type-stats`,
    }),
    getFinancialsData: builder.query({
      query: () => `admin/get-financials-data`,
    }),
    getVehicleDataForDriver: builder.query({
      query: () => `admin/get-vehicle-stats-for-drivers`,
    }),
    getAllFaqs: builder.query({
      query: () => `admin/faq/getall`,
    }),
    deleteOneFaq: builder.mutation({
      query(body) {
        return {
          url: `admin/faq/deleteone`,
          method: "DELETE",
          body,
        };
      },
    }),
    updateOneFaq: builder.mutation({
      query(body) {
        return {
          url: `admin/faq/updateone`,
          method: "PATCH",
          body,
        };
      },
    }),
    createFaq: builder.mutation({
      query(body) {
        return {
          url: `admin/faq/create`,
          method: "POST",
          body,
        };
      },
    }),
    updateVehicleDataFromDriver: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `admin/update-vehicle-data-from-driver/${id}`,
          method: "PATCH",
          body,
        };
      },
    }),
    updateRentalVehicleDataById: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `vehicle/update-vehicle-by-id/${id}`,
          method: "PUT",
          body,
        };
      },
    }),

    updateTripById: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `admin/update-trip/${id}`,
          method: "PUT",
          body,
        };
      },
    }),

    updateBookingById: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `car-rental/bookings/${id}`,
          method: "PUT",
          body,
        };
      },
    }),
    updateRentalVehiclePricing: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `car-rental/admin/vehicle-pricing`,
          method: "POST",
          body,
        };
      },
    }),
    updateVehicleInfo: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `vehicle-info/${id}`,
          method: "PUT",
          body,
        };
      },
    }),
    forgetPassword: builder.mutation({
      query({ ...body }) {
        return {
          url: `admin/forget-password`,
          method: "POST",
          body,
        };
      },
    }),
    verificationCodeVerification: builder.mutation({
      query({ ...body }) {
        return {
          url: `admin/verify-verification-code`,
          method: "POST",
          body,
        };
      },
    }),
    forgetResetPasswordCall: builder.mutation({
      query({ ...body }) {
        return {
          url: `admin/forget-reset-password`,
          method: "POST",
          body,
        };
      },
    }),

    addDrivessr: builder.mutation({
      query({ ...body }) {
        return {
          url: `driver/create`,
          method: "POST",
          body,
        };
      },
    }),

    getSwitchFetchTitle: builder.query({
      query: ({ BankCode, BankName, MSISDN, Amount, AccountNumber, countryCode }) => ({
        url: `swich/fetch-title`,
        params: {
          BankCode,
          BankName,
          MSISDN,
          Amount,
          AccountNumber,
          countryCode,
        }
      }),
    }),

    UpdateDriverSwitchIBFT: builder.mutation({
      query(body) {
        return {
          url: `swich/ibft`,
          method: "POST",
          body,
        };
      },
    }),
    assignDriverToBooking: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `car-rental/admin/bookings/${id}/assign-driver`,
          method: "PUT",
          body,
        };
      },
    }),

    updateAdditionalChargesToBooking: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `car-rental/admin/bookings/${id}/additional-charges`,
          method: "PUT",
          body,
        };
      },
    }),

    updateValidateToBooking: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `car-rental/admin/bookings/${id}/validate`,
          method: "PUT",
          body,
        };
      },
    }),
    updateAdditionalChargesToBooking: builder.mutation({
      query({ id, ...body }) {
        return {
          url: `car-rental/admin/bookings/${id}/additional-charges`,
          method: "PUT",
          body,
        };
      },
    }),
    getTripDetails: builder.query({
      query: ({ tripId }) => ({
        url: `trips/get-trip-details`,
        params: {
          tripId
        }
      }),
    }),

    getCourierDriversList: builder.query({
      query: ({ tripId }) => ({
        url: `trips/active-drivers-for-trip`,
        params: {
          tripId,
        }
      }),
    }),

updateAssignCourierDriver: builder.mutation({
  query: ({ tripId, driverId, distance, time, arrivalDistance, arrivalTime }) => {
    const parsedDistance = typeof distance === 'string' ? parseFloat(distance) : distance;
    const parsedTime = typeof time === 'string' ? parseFloat(time) : time;
    const parsedArrivalDistance = arrivalDistance !== undefined ? (typeof arrivalDistance === 'string' ? parseFloat(arrivalDistance) : arrivalDistance) : parsedDistance;
    const parsedArrivalTime = arrivalTime !== undefined ? (typeof arrivalTime === 'string' ? parseFloat(arrivalTime) : arrivalTime) : parsedTime;
    
    return {
      url: `trips/assign-to-driver/${tripId}/${driverId}`,
      method: "PUT",
      body: {
        distance: parsedDistance,
        time: parsedTime,
        arrivalDistance: parsedArrivalDistance,
        arrivalTime: parsedArrivalTime,
      },
    };
  },
}),

    addAreaPolygon: builder.mutation({
      query(body) {
        return {
          url: `fare/add-area-polygon`,
          method: "POST",
          body,
        };
      },
    }),

    getAreasByCity: builder.query({
      query: (city) => ({
        url: `fare/get-areas-by-city/${city || 'Karachi'}`,
        method: 'GET',
      }),
    }),

    updatePaymentStripe: builder.mutation({
      query({ bookingId, paymentIntentId }) {
        return {
          url: `/car-rental/bookings/${bookingId}/confirm/stripe`, // URL = booking ID
          method: "PUT",
          body: { paymentIntentId }, // body = paymentIntentId
        };
      },
    }),

    getAllDriversTrips: builder.query({
      query: ({ driver, fromDate, toDate, status } = {}) => ({
        url: `admin/get-all-driver-trips`,
        params: {
          driver: driver,
          fromDate: fromDate,
          toDate: toDate,
          status: status,
        }
      }),
      }),
    
      
  updateDriverOtp: builder.mutation({
      query(body) {
        return {
          url: `driver/otp-verify`,
          method: "POST",
          body,
        };
      },
    }),

    resendDriverOtp: builder.mutation({
      query(body) {
        return {
          url: `driver/otp-request`,
          method: "POST",
          body,
        };
      },
    }),

    getDriverIncentiveReport: builder.query({
      query: ({  startDate, endDate, refCode, search, skip, limit }) => ({
        url: `wallet/driver-wallet-report`,
        params: {
          limit: limit || 0,
          skip: skip || 0,
          type: 'deposit',           //incentive
          startDate:startDate, //'2026-04-01'
          endDate: endDate,
          refCode: refCode,
          search:search,

        }
      }),
    }),

    getFranchiseDriversSummary: builder.query({
      query: ({ driver, fromDate, toDate, refCode }) => ({
        url: `admin/franchise-dashboard/trip-status`,
        params: {
          driver:driver,
          fromDate:fromDate, 
          toDate: toDate,
          refCode:refCode, // ,'FR-01'
          timezone:'Asia/Karachi',

        }
      }),
    }),
    
    // getSwitchIBFT: builder.query({
    //       query: ({ accountTitle, accountNumber, bankCode, bankName,customerTransactionId,name,cnic,msisdn,amount,currency,countryCode }) => ({
    //         url: `swich/ibft`,
    //         params: {
    //           accountTitle,
    //           accountNumber,
    //           bankCode,
    //           bankName,
    //           customerTransactionId,
    //           name,
    //           cnic,
    //           msisdn,
    //           amount,
    //           currency,
    //           countryCode,
    //         }
    //       }),
    //     }),
    // use axios directly for image upload
  }),
});

export const uploadImage = async (data) => {
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API_URL}upload/public`,
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

export const addDriver = async (data) => {
  const axios = require("axios");
  const config = {
    method: "POST",
    // url: "https://forunpoint-pakistan-815595f6cd55.herokuapp.com/v1/api/driver/driver-by-admin",
    url: `${process.env.REACT_APP_BASE_API_URL}driver/driver-by-admin`,
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify(data),
  };

  try {

    let returnObject = {
      status: false,
      message: "",
      data: null,
    }

    const response = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
      body: config.data,
    });
    const responseData = await response.json();
    console.log("Response Data:", responseData);
    if (!response.ok) {
      returnObject.status = false;
      returnObject.message = responseData.message || "Error occurred";
      returnObject.data = responseData;
      console.log("Error:", returnObject);
      return returnObject;
    }
    returnObject.status = true;
    returnObject.message = "Driver added successfully";
    returnObject.data = responseData;
    console.log("Success:", returnObject);
    return returnObject;


  } catch (error) {
    console.error("Error:", error.response?.data || error.message);
    returnObject = {
      status: false,
      message: error.response?.data || error.message,
      data: null,
    };

    return returnObject;
  }
};

export const loginDriver = async (data) => {
  const axios = require("axios");

  let config = {
    method: "post",
    maxBodyLength: Infinity,

    url: `${process.env.REACT_APP_BASE_API_URL}driver/login`,
    headers: {
      "Content-Type": "application/json",
      authorization: "fc3d9682-8f1e-45a0-905d-d6d23155421e",
    },
    data: JSON.stringify(data),
  };

  return axios(config)
    .then(function (response) {
      console.log(JSON.stringify(response.data));
      return response.data;
    })
    .catch(function (error) {
      console.log(error);
    });
};


export const createDocument = async (data, token) => {
  const axios = require("axios");

  let config = {
    method: "post",
    maxBodyLength: Infinity,
    url: `${process.env.REACT_APP_BASE_API_URL}document/create`,
    headers: {
      "Content-Type": "application/json",
      authorization: token,
    },
    data: JSON.stringify(data),
  };

  let response_info = {
    status: false,
    message: "",
    data: null,
  };

  try {
    const reposne = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
      body: config.data,
    });
    const responseData = await reposne.json();
    console.log("Response Data:", responseData);
    if (!reposne.ok) {
      response_info.status = false;
      response_info.message = responseData.message || "Error occurred";
      response_info.data = responseData;
      console.log("Error:", response_info);
      return response_info;
    }
    response_info.status = true;
    response_info.message = "Document added successfully";
    response_info.data = responseData;
    console.log("Success:", response_info);
    return response_info;

  }
  catch (error) {
    console.log("error", error);
    response_info.status = false;
    response_info.message = error.response?.data || error.message;
    response_info.data = null;
    return response_info;
  }


  return response_info


  // return axios(config)




}


export const addVahicle = async (data, token) => {
  console.log("data", data);
  console.log("token", token);

  let config = {
    method: "POST",
    maxBodyLength: Infinity,
    url: `${process.env.REACT_APP_BASE_API_URL}vehicle/create`,
    headers: {
      "Content-Type": "application/json",
      authorization: token,
    },
    data: JSON.stringify(data),
  };

  let response_info = {
    status: false,
    message: "",
    data: null,

  }

  try {
    const reposne = await fetch
      (config.url, {
        method: config.method,
        headers: config.headers,
        body: config.data,
      });
    const responseData = await reposne.json();
    console.log("Response Data:", responseData);
    if (!reposne.ok) {
      response_info.status = false;
      response_info.message = responseData.message || "Error occurred";
      response_info.data = responseData;
      console.log("Error:", response_info);
      return response_info;
    }
    response_info.status = true;
    response_info.message = "Driver added successfully";
    response_info.data = responseData;
    console.log("Success:", response_info);
    return response_info;

  }
  catch (error) {
    console.log("error", error);
    response_info.status = false;
    response_info.message = error.response?.data || error.message;
    response_info.data = null;
    return response_info;
  }
};

// export const getCourierDriversList = async (tripId,token) => {
//   const axios = require("axios");

//   let config = {
//     method: "GET",
//     maxBodyLength: Infinity,
//     url: `https://forunpoint-pakistan-815595f6cd55.herokuapp.com/v1/api/trips/active-drivers?tripId=${tripId}`,
//     headers: {
//       "Content-Type": "application/json",
//       authorization: token,
//     },
//   };

//   let response_info = {
//     status: false,
//     message: "",
//     data: null,
//   }

//   try {
//     const reposne = await fetch(config.url, {
//       method: config.method,
//       headers: config.headers,
//     });
//     const responseData = await reposne.json();
//     console.log("Response Data:", responseData);
//     if (!reposne.ok) {
//       response_info.status = false;
//       response_info.message = responseData.message || "Error occurred";
//       response_info.data = responseData;
//       console.log("Error:", response_info);
//       return response_info;
//     }
//     response_info.status = true;
//     response_info.message = "Driver added successfully";
//     response_info.data = responseData;
//     console.log("Success:", response_info);
//     return response_info;

//   }
//   catch (error) {
//     console.log("error", error);
//     response_info.status = false;
//     response_info.message = error.response?.data || error.message;
//     response_info.data = null;
//     return response_info;
//   }
//   return response_info

// };


export const listAreas = async (token) => {
  const axios = require("axios");

  let config = {
    method: "GET",
    maxBodyLength: Infinity,
    url: `${process.env.REACT_APP_BASE_API_URL}fare/get-all-areas`,
    headers: {
      "Content-Type": "application/json",
      authorization: token,
    },
  };

  let response_info = {
    status: false,
    message: "",
    data: null,
  }

  try {
    const reposne = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
    });
    const responseData = await reposne.json();
    console.log("Response Data:", responseData);
    if (!reposne.ok) {
      response_info.status = false;
      response_info.message = responseData.message || "Error occurred";
      response_info.data = responseData;
      console.log("Error:", response_info);
      return response_info;
    }
    response_info.status = true;
    response_info.message = "Driver added successfully";
    response_info.data = responseData;
    console.log("Success:", response_info);
    return response_info;

  }
  catch (error) {
    console.log("error", error);
    response_info.status = false;
    response_info.message = error.response?.data || error.message;
    response_info.data = null;
    return response_info;
  }
  return response_info

};

export const getRentalBookingAll = async (token) => {
  const axios = require("axios");
  //console.log('Rental API call',process.env.RENTAL_API_URL)
  let config = {
    method: "GET",
    maxBodyLength: Infinity,
    url: `https://foruntest-174e1b08fe50.herokuapp.com/v1/api/car-rental/bookings`,
    headers: {
      "Content-Type": "application/json",
      authorization: 'f344d78e-1868-44bb-96c2-baf7c348ca00', //token,
    },
  };

  let response_info = {
    status: false,
    message: "",
    data: null,
  }

  try {
    const reposne = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
    });
    const responseData = await reposne.json();
    console.log("Response Data:", responseData);
    if (!reposne.ok) {
      response_info.status = false;
      response_info.message = responseData.message || "Error occurred";
      response_info.data = responseData;
      console.log("Error:", response_info);
      return response_info;
    }
    response_info.status = true;
    response_info.message = "Driver added successfully";
    response_info.data = responseData;
    console.log("Success:", response_info);
    return response_info;

  }
  catch (error) {
    console.log("error", error);
    response_info.status = false;
    response_info.message = error.response?.data || error.message;
    response_info.data = null;
    return response_info;
  }
  return response_info

};

export const updateAreaDetails = async (data, id) => {
  //fare/update-area/67fa09c86280225c0ca2ce02

  console.log(data)

  const axios = require("axios");
  let config = {
    method: "PUT",
    maxBodyLength: Infinity,
    url: `${process.env.REACT_APP_BASE_API_URL}fare/update-area/${id}`,
    headers: {
      "Content-Type": "application/json",

    },
    data: JSON.stringify(data),
  };
  let response_info = {
    status: false,
    message: "",
    data: null,
  }
  try {
    const reposne = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
      body: config.data,
    });
    const responseData = await reposne.json();
    console.log("Response Data:", responseData);
    if (!reposne.ok) {
      response_info.status = false;
      response_info.message = responseData.message || "Error occurred";
      response_info.data = responseData;
      console.log("Error:", response_info);
      return response_info;
    }
    response_info.status = true;
    response_info.message = "Driver added successfully";
    response_info.data = responseData;
    console.log("Success:", response_info);
    return response_info;

  }
  catch (error) {
    console.log("error", error);
    response_info.status = false;
    response_info.message = error.response?.data || error.message;
    response_info.data = null;
    return response_info;
  }
  return response_info


}

export const createAreaDetails = async (data) => {
  const axios = require("axios");
  let config = {
    method: "POST",
    maxBodyLength: Infinity,
    url: `${process.env.REACT_APP_BASE_API_URL}fare/add-area`,
    headers: {
      "Content-Type": "application/json",
    },
    data: JSON.stringify(data),
  };

  let response_info = {
    status: false,
    message: "",
    data: null,
  };

  try {
    const response = await fetch(config.url, {
      method: config.method,
      headers: config.headers,
      body: config.data,
    });

    const responseData = await response.json();
    console.log("Response Data:", responseData);

    if (!response.ok) {
      response_info.status = false;
      response_info.message = responseData.message || "Error occurred while creating area";
      response_info.data = responseData;
      console.log("Error:", response_info);
      return response_info;
    }

    response_info.status = true;
    response_info.message = "Area created successfully";
    response_info.data = responseData;
    console.log("Success:", response_info);
    return response_info;

  } catch (error) {
    console.log("error", error);
    response_info.status = false;
    response_info.message = error.response?.data?.message || error.message || "Network error occurred";
    response_info.data = null;
    return response_info;
  }
};


// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetDriverCountQuery,
  useGetDriverCountByCategoryQuery,
  useGetRiderCountQuery,
  useGetVehicleTypesQuery,
  useLoginUserMutation,
  useDeleteUserMutation,
  useCreateUserMutation,
  useCreateDriverSubscriptionMutation,
  useUpdateDriverWithdrawlMutation,
  useUpdateDriverDepositMutation,
  useLogoutUserMutation,
  useGetAllDriversQuery,
  useGetAllDriversOnlineVehicleWiseQuery,
  useGetAllUploadedPaymentsQuery,
  useGetDriverByIdQuery,
  useLazyGetDriverByIdQuery,
  useLazyGetDriversLogByIdQuery,
  useGetAllUsersQuery,
  useGetAllAdminsQuery,
  useUpdateSingleAdminMutation,
  useUpdateSingleDriverMutation,
  useGetDriversLocalizationCategoriesQuery,
  useGetUsersLocalizationCategoriesQuery,
  useUpdateSingleUserMutation,
  useGetTripStatsQuery,
  useGetAllTripsQuery,
  useGetDriversWalletQuery,
  useGetAllTripsExportQuery,
  useLazyGetAllTripsExportQuery,
  useLazyGetAllDriversSearchQuery,
  useLazyGetSingleDriverWalletQuery,
  useGetSubscribedDriverbyIdMutation,
  useUpdateDriverPasswordMutation,
  useGetVehicleTypeStatsQuery,
  useGetVehicleInfoQuery,
  useGetFinancialsDataQuery,
  useGetVehicleDataForDriverQuery,
  useGetAllFaqsQuery,
  useCreateFaqMutation,
  useDeleteOneFaqMutation,
  useUpdateOneFaqMutation,
  useUpdateVehicleDataFromDriverMutation,
  useUpdateBookingByIdMutation,
  useUpdateVehicleInfoMutation,
  useForgetPasswordMutation,
  useVerificationCodeVerificationMutation,
  useForgetResetPasswordCallMutation,
  useLazyGetAllDriversExportQuery,
  useCreateVehicleByAdminMutation,
  useAddDriverMutation,
  useGetDriverDocumentsByIdQuery,
  useGetDriverProfileByIdQuery,
  useGetDriverVehicleByIdQuery,
  useLazyGetVehicleSwitchByIdQuery,
  useUpdateDriverDocByAdminIdMutation,
  useVehicleCreateByAdminByIdMutation,
  useGetRentalAllBookingQuery,
  useLazyGetSwitchFetchTitleQuery,
  useUpdateDriverSwitchIBFTMutation,
  useLazyGetRentalAvailableDriversQuery,
  useAssignDriverToBookingMutation,
  useLazyGetDriversRegReportQuery,
  useGetDriversEarningReportQuery,
  useGetDriversEarningsReportQuery,
  useUpdateAdditionalChargesToBookingMutation,
  useGetTripDetailsQuery,
  useUpdateValidateToBookingMutation,
  useAddAreaPolygonMutation,
  useGetAreasByCityQuery,
  useUpdatePaymentStripeMutation,
  useGetAllRentalVehiclesQuery,
  useUpdateRentalVehiclePricingMutation,
  useGetVehicleByIdQuery,
  useUpdateRentalVehicleDataByIdMutation,
  useLazyGetCourierDriverListQuery,
  useLazyGetAllDriversTripsQuery,
  useLazyGetCourierDriversListQuery,
  useUpdateAssignCourierDriverMutation,
  useUpdateTripByIdMutation,
  useUpdateDriverOtpMutation,
  useResendDriverOtpMutation,
  useGetDriversWithdrawalQuery,
  useGetDriverIncentiveReportQuery,
  useGetFranchiseDriversSummaryQuery,
} = Api;
